import classNames from 'classnames';
import React, { useState } from 'react';
import Tag from 'components/styleguide/tag/Tag';
import { GateCheck, GateResult, effectiveGateVerdict } from './utils/curationScoring';
import { gateCheckDetail, gateCheckLabel, STATUS_LABELS } from './utils/gateCheckPresenter';
import StatusOverrideGroup from './StatusOverrideGroup';

const styles = require('./GateCheckRow.module.scss');

const TAG_VARIANTS: Record<GateResult, 'success' | 'error' | 'warning'> = {
  fail: 'error',
  na: 'warning',
  pass: 'success',
};

const TAG_ICONS: Record<GateResult, string> = {
  fail: 'x',
  na: 'circleHelp',
  pass: 'check',
};

export interface Props {
  check: GateCheck;
  disabled?: boolean;
  onOverride: (key: string, value: GateResult | null) => void;
}

const OVERRIDE_OPTIONS: { label: string; value: GateResult }[] = [
  { label: 'Pass', value: 'pass' },
  { label: 'Fail', value: 'fail' },
  { label: 'N/A', value: 'na' },
];

// One eligibility check: the pill shows the effective verdict; "override"
// discloses the Pass/Fail/N/A control. An overridden row names the
// platform's original finding so every verdict stays inspectable.
export default function GateCheckRow({ check, disabled = false, onOverride }: Props) {
  const [overriding, setOverriding] = useState(false);
  const effective = effectiveGateVerdict(check);
  const label = gateCheckLabel(check);

  const onChange = (value: GateResult | null) => {
    onOverride(check.key, value);
    setOverriding(false);
  };

  return (
    <li className={styles['ui-gate-check']}>
      <div className={styles['ui-gate-check__info']}>
        <span className={styles['ui-gate-check__label']}>{label}</span>
        <span className={styles['ui-gate-check__detail']}>{gateCheckDetail(check)}</span>
        {check.override !== null && (
          <span className={styles['ui-gate-check__overridden']}>
            Curator override: platform said {STATUS_LABELS[check.verdict]}.
          </span>
        )}
      </div>
      <div className={styles['ui-gate-check__status']}>
        <Tag iconLeft={TAG_ICONS[effective]} variant={TAG_VARIANTS[effective]}>
          {STATUS_LABELS[effective]}
        </Tag>
        {!disabled &&
          (overriding ? (
            <StatusOverrideGroup
              baseValue={check.verdict}
              label={`Override result for ${label}`}
              onChange={onChange}
              options={OVERRIDE_OPTIONS}
              override={check.override}
            />
          ) : (
            <button
              className={classNames(styles['ui-gate-check__override-link'])}
              onClick={() => setOverriding(true)}
              type="button"
            >
              override
            </button>
          ))}
      </div>
    </li>
  );
}
